import { Component, computed, effect, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, of } from 'rxjs';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay';
import { EtatService } from '../../Services/etat';
import { MeteoService, MeteoData } from '../../Services/meteo';
import { ParcService } from '../../Services/parc';
import { EnergieUnitePipe, PuissanceUnitePipe, DeviseUnitePipe, Co2UnitePipe } from '../../Pipes/unite.pipe';

@Component({
  selector: 'app-parc-resume',
  imports: [SpinnerOverlayComponent, EnergieUnitePipe, PuissanceUnitePipe, DeviseUnitePipe, Co2UnitePipe],
  templateUrl: './parc-resume.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parc-resume.css',
})
export class ParcResumeComponent {
  private readonly etat = inject(EtatService);
  private readonly meteoService = inject(MeteoService);
  private readonly parcService = inject(ParcService);
  readonly chargement = signal(false);
  readonly erreur = signal('');
  readonly puissanceMoyenne = signal<number | null>(null);
  readonly energieCumulee = signal<number | null>(null);
  readonly co2evite = signal<number | null>(null);
  readonly gainsCumules = signal<number | null>(null);

  private readonly chiffresUtiles = effect((onCleanup) => {
    const parc = this.etat.parcSelectionne();
    let actif = true;
    onCleanup(() => { actif = false; });
    const estValide = () => actif && parc === this.etat.parcSelectionne();
    this.puissanceMoyenne.set(null);
    this.energieCumulee.set(null);
    this.co2evite.set(null);
    this.gainsCumules.set(null);
    this.erreur.set('');
    this.chargement.set(!!parc);
    if (!parc) return;
    void this.parcService.getChiffresUtiles(parc.id).then(data => {
      if (!estValide()) return;
      this.puissanceMoyenne.set(data?.puissance ?? null);
      this.energieCumulee.set(data?.energie_cumulee ?? null);
      this.co2evite.set(data?.co2_evite ?? null);
      this.gainsCumules.set(data?.revenus_cumules ?? null);
    }).catch(() => {
      if (estValide()) this.erreur.set('Impossible de charger les chiffres du parc.');
    }).finally(() => {
      if (estValide()) this.chargement.set(false);
    });
  });

  /** Coordonnées de la première installation avec géolocalisation valide. */
  private readonly coords = computed(() => {
    const inst = this.etat
      .installations()
      .find(
        (i) => Number.isFinite(i.lat) && Number.isFinite(i.lon) && !(i.lat === 0 && i.lon === 0),
      );
    return inst ? { lat: inst.lat, lon: inst.lon } : null;
  });

  /** Données météo temps réel, null tant que les coordonnées ne sont pas disponibles. */
  readonly meteo = toSignal(
    toObservable(this.coords).pipe(
      switchMap((c) => (c ? this.meteoService.getMeteo(c.lat, c.lon) : of(null))),
    ),
    { initialValue: null as MeteoData | null },
  );

  /** Libellé du parc sélectionné. */
  readonly nomParc = computed(() => this.etat.parcSelectionne()?.libelle ?? '');

  /** Date du jour formatée en français, ex. "Lundi 23 mai". */
  readonly dateLabel = new Date()
    .toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
    .replace(/^\w/, (c) => c.toUpperCase());
}
